/**
 * «Взять на вахту» (МП-13, МП-16): одной кнопкой скачать в телефон то, что нужно на смену — план дня, «Мой день»,
 * открытые замечания и шаблоны чек-листов. Своего хранилища нет: страница читает адреса обычным запросом, а
 * служебный обработчик (public/sw.js) запоминает ответы и отдаёт их без связи. Здесь — список адресов, выбор частей,
 * учёт «когда сохранено и сколько места занято» и правило «по Wi-Fi сразу, по мобильной сети — если разрешено».
 * Ответы с суммами не сохраняются: в списке нет адресов с деньгами.
 */

export type WatchPart = 'plan' | 'remarks' | 'checklists';

export const WATCH_PARTS: Array<{ value: WatchPart; label: string; hint: string }> = [
  { value: 'plan', label: 'План дня и мои участки', hint: 'Сегодня и неделя вперёд по моим проектам' },
  { value: 'remarks', label: 'Открытые замечания', hint: 'Закрытые на телефон не берутся' },
  { value: 'checklists', label: 'Шаблоны чек-листов', hint: 'Действующие шаблоны для проверок на площадке' },
];

export interface WatchMeta {
  savedAt: number;
  parts: WatchPart[];
  bytes: number;
  files: number;
  failed: number;
}

export interface WatchSettings {
  parts: WatchPart[];
  /** Обновлять и по мобильной сети (по умолчанию — только по Wi-Fi) */
  allowMobile: boolean;
}

export const DEFAULT_WATCH_SETTINGS: WatchSettings = {
  parts: ['plan', 'remarks', 'checklists'],
  allowMobile: false,
};

export const META_PREFIX = 'epc-watch-meta:';
const SETTINGS_PREFIX = 'epc-watch-settings:';
/** Обновлять сохранённое при открытии «Моего дня», если ему больше этого времени */
export const REFRESH_AFTER_MS = 15 * 60 * 1000;

type Scope = { tenantId: string; userId: string };
const key = (prefix: string, s: Scope) => `${prefix}${s.tenantId}:${s.userId}`;

function storage(): Storage | null {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage;
  } catch {
    return null;
  }
}

export function readWatchMeta(scope: Scope): WatchMeta | null {
  try {
    const raw = storage()?.getItem(key(META_PREFIX, scope));
    if (!raw) return null;
    const m = JSON.parse(raw) as WatchMeta;
    return typeof m.savedAt === 'number' ? m : null;
  } catch {
    return null;
  }
}

function writeWatchMeta(scope: Scope, meta: WatchMeta) {
  try {
    storage()?.setItem(key(META_PREFIX, scope), JSON.stringify(meta));
  } catch {
    /* хранилище недоступно — экран работает без отметки */
  }
}

export function readWatchSettings(scope: Scope): WatchSettings {
  try {
    const raw = storage()?.getItem(key(SETTINGS_PREFIX, scope));
    if (!raw) return DEFAULT_WATCH_SETTINGS;
    const v = JSON.parse(raw) as Partial<WatchSettings>;
    const known = new Set(WATCH_PARTS.map((p) => p.value));
    const parts = Array.isArray(v.parts) ? v.parts.filter((p): p is WatchPart => known.has(p)) : [];
    return { parts, allowMobile: v.allowMobile === true };
  } catch {
    return DEFAULT_WATCH_SETTINGS;
  }
}

export function writeWatchSettings(scope: Scope, settings: WatchSettings) {
  try {
    storage()?.setItem(key(SETTINGS_PREFIX, scope), JSON.stringify(settings));
  } catch {
    /* не страшно: настройки останутся только на экране */
  }
}

/** Стереть отметки вместе с запомненным (выход, другой человек на телефоне) */
export function clearWatchMeta() {
  try {
    const s = storage();
    if (!s) return;
    const mine: string[] = [];
    for (let i = 0; i < s.length; i += 1) {
      const k = s.key(i);
      if (k?.startsWith(META_PREFIX)) mine.push(k);
    }
    for (const k of mine) s.removeItem(k);
  } catch {
    /* нечего стирать */
  }
}

export type NetworkKind = 'wifi' | 'mobile' | 'unknown';

/** Какая сеть сейчас; если браузер не сообщает (iOS Safari, Firefox) — «неизвестно» */
export function networkKind(
  conn: { type?: string; saveData?: boolean } | undefined = (
    navigator as unknown as { connection?: { type?: string; saveData?: boolean } }
  ).connection,
): NetworkKind {
  if (conn?.type === 'cellular' || conn?.saveData === true) return 'mobile';
  if (conn?.type === 'wifi' || conn?.type === 'ethernet') return 'wifi';
  return 'unknown';
}

/**
 * Можно ли обновлять само: нужна связь; без разрешения — только когда точно известно, что это Wi-Fi.
 * Кнопка «Обновить сейчас» от этого правила не зависит: человек сам решил.
 */
export function mayRefreshNow(settings: WatchSettings, online: boolean, kind: NetworkKind): boolean {
  return online && (kind === 'wifi' || settings.allowMobile);
}

/** Есть ли в браузере сохранённая копия (отметка без копии — после выхода — ничего не обещает) */
export async function hasSavedCopy(): Promise<boolean> {
  try {
    if (typeof caches === 'undefined') return false;
    const cache = await caches.open('epc-site-data-v1');
    const keys = await cache.keys();
    return keys.some((k) => new URL(k.url).pathname.startsWith('/api/v1/site/my-day'));
  } catch {
    return false;
  }
}

/** Пора ли обновлять: сохранённое старше срока или нет совсем */
export function isStale(meta: WatchMeta | null, now: number): boolean {
  return !meta || now - meta.savedAt >= REFRESH_AFTER_MS;
}

export interface WatchProject {
  id: string;
}

type Fetcher = (url: string) => Promise<{ ok: boolean; size: number; json: () => Promise<unknown> }>;

export const realFetcher: Fetcher = async (url) => {
  const res = await fetch(url, { credentials: 'same-origin', headers: { accept: 'application/json' } });
  // Обработчик отдал старую копию, потому что связь зависла: это не обновление
  if (res.headers.get('x-epc-offline')) return { ok: false, size: 0, json: async () => null };
  const blob = await res.blob();
  return { ok: res.ok, size: blob.size, json: async () => JSON.parse(await blob.text()) };
};

/** Адреса, которые нужны для выбранных частей (шаблоны чек-листов — по списку, он читается первым) */
export function watchUrls(parts: WatchPart[], projects: WatchProject[], date: string): string[] {
  const urls: string[] = [];
  if (parts.includes('plan')) {
    urls.push(`/api/v1/site/my-day?date=${date}&days=7`, `/api/v1/site/my-day?date=${date}&days=1`);
    for (const p of projects) urls.push(`/api/v1/site/projects/${p.id}/day-plan?date=${date}`);
  }
  if (parts.includes('remarks'))
    for (const p of projects) urls.push(`/api/v1/supervision/remarks?projectId=${p.id}&status=open`);
  if (parts.includes('checklists')) urls.push('/api/v1/quality/checklist-templates?status=active');
  return urls;
}

/**
 * Скачать выбранное. Ошибка одного адреса не отменяет остальное: считаем, что не вышло, и идём дальше
 * (у человека может не быть права на часть данных — это не сбой).
 */
export async function takeOnWatch(
  scope: Scope,
  settings: WatchSettings,
  projects: WatchProject[],
  date: string,
  fetcher: Fetcher = realFetcher,
  now: () => number = Date.now,
  /** Вернёт false, если страницу закрыли или человек вышел: отметка тогда не пишется */
  stillCurrent: () => boolean = () => true,
): Promise<WatchMeta> {
  let bytes = 0;
  let files = 0;
  let failed = 0;
  const get = async (url: string) => {
    try {
      const r = await fetcher(url);
      if (!r.ok) {
        failed += 1;
        return null;
      }
      bytes += r.size;
      files += 1;
      return r;
    } catch {
      failed += 1;
      return null;
    }
  };
  for (const url of watchUrls(settings.parts, projects, date)) {
    const r = await get(url);
    if (r && url.startsWith('/api/v1/quality/checklist-templates')) {
      let list: unknown = [];
      try {
        list = await r.json();
      } catch {
        failed += 1;
      }
      const ids = Array.isArray(list)
        ? list.map((t) => (t as { id?: unknown }).id).filter((id): id is string => typeof id === 'string')
        : [];
      for (const id of ids) await get(`/api/v1/quality/checklist-templates/${id}`);
    }
  }
  const meta: WatchMeta = { savedAt: now(), parts: settings.parts, bytes, files, failed };
  // Отметка ставится, только если что-то сохранилось: иначе экран обещал бы копию, которой нет
  if (files > 0 && stillCurrent()) writeWatchMeta(scope, meta);
  return meta;
}

export function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} Б`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} КБ`;
  return `${(bytes / 1024 / 1024).toFixed(1)} МБ`;
}

/** Занято и доступно на устройстве, если браузер сообщает */
export async function deviceStorage(): Promise<{ used: number; quota: number } | null> {
  try {
    const e = await navigator.storage?.estimate?.();
    return e?.usage != null && e.quota != null ? { used: e.usage, quota: e.quota } : null;
  } catch {
    return null;
  }
}
